import { useState, useEffect } from 'react';
import { Form } from 'antd';
import { useNavigate } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '@hooks/redux';

import { getAccountBalanceApi, getSenderAccountsApi } from '@http/accounts';
import { electricTransactionApi } from '@http/payments';

import Currencies from '@components/Currency/OrienbankRates';
import SenderAccountsModal from '@components/Payments/modals/SenderAccountsModal';
import ConfirmPaymentModal from '@components/Payments/modals/ConfirmPaymentModal';

import { isConfirmPaymentModalOpenAC, electricQueryAC, electricClientInfoAC } from '@store/reducers/payments';
import {
  senderAccountBalanceAC,
  senderAccountsModalOpenAC,
  selectedSenderAccountAC,
  toAccountBalanceAC,
} from '@store/reducers/accounts';
import { otpConfirmAC, repeatElectricTransactionAC, verifyTransIdAC } from '@store/reducers/transactions';
import ElectricForm from '@components/Payments/forms/ElectricForm';
import { useTranslation } from 'react-i18next';

function Electric() {
  const { t } = useTranslation();
  const [form] = Form.useForm();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  const { senderAccounts } = useAppSelector(state => state.accountsReducer);
  const repeatElectricTransaction = useAppSelector(state => state.transactionsReducer.repeatElectricTransaction);
  const electricQuery = useAppSelector(state => state.paymentsReducer.electricQuery);

  const [senderAccountCurrency, setSenderAccountCurrency] = useState('');
  const [toAccountCurrency, setToAccountCurrency] = useState('');

  useEffect(() => {
    initializeData();

    return clearFields;
  }, []);

  useEffect(() => {
    repeatElectricTransactionHandler();
  }, [repeatElectricTransaction]);

  useEffect(() => {
    dispatch(otpConfirmAC(false));
  }, [electricQuery]);

  const senderAccountSelect = (account: any) => {
    setToAccountCurrency('');
    setSenderAccountCurrency(account.ccy);
    dispatch(electricQueryAC({ ...electricQuery, sending_ac: account.value }));

    form.setFieldValue('sending_ac', account.value);

    dispatch(senderAccountsModalOpenAC(false));
    dispatch(selectedSenderAccountAC(account.value));
    dispatch(getAccountBalanceApi('sender', { cust_ac_no: account.value }));
    dispatch(toAccountBalanceAC(0));
  };

  const electricTransaction = () => {
    const { sending_amount, receiving_ac, region, ...filteredQuery } = electricQuery;

    dispatch(electricTransactionApi(filteredQuery, navigate, () => clearFields()));
  };

  const initializeData = () => {
    dispatch(selectedSenderAccountAC({}));
    dispatch(senderAccountBalanceAC(0));
    dispatch(toAccountBalanceAC(0));
    dispatch(
      getSenderAccountsApi({
        transaction_type: 'ELECTRIC',
      })
    );
  };

  const repeatElectricTransactionHandler = () => {
    if (Object.keys(repeatElectricTransaction).length > 0) {
      setSenderAccountCurrency(repeatElectricTransaction.sending_ccy);
      setToAccountCurrency(repeatElectricTransaction.sending_ccy);
      dispatch(
        electricQueryAC({
          sending_ac: repeatElectricTransaction.sending_ac,
          sending_amount: '',
          narrative: repeatElectricTransaction.narrative,
        })
      );

      form.setFieldValue('sending_ac', repeatElectricTransaction.sending_ac);

      form.setFieldValue('narrative', repeatElectricTransaction.narrative);

      dispatch(selectedSenderAccountAC(repeatElectricTransaction.sending_ac));
      dispatch(getAccountBalanceApi('sender', { cust_ac_no: repeatElectricTransaction.sending_ac }));
    }
  };

  const clearFields = () => {
    form.resetFields();

    setSenderAccountCurrency('');
    dispatch(isConfirmPaymentModalOpenAC(false));
    dispatch(selectedSenderAccountAC({}));
    dispatch(senderAccountBalanceAC(0));
    dispatch(otpConfirmAC(false));
    dispatch(verifyTransIdAC(''));
    dispatch(repeatElectricTransactionAC({}));
    dispatch(electricQueryAC({}));
    dispatch(electricClientInfoAC({}));
  };

  return (
    <div className='md:grid grid-cols-6 gap-10 mt-5'>
      <div className='col-span-4'>
        <ElectricForm form={form} senderAccountCurrency={senderAccountCurrency} toAccountCurrency={toAccountCurrency} />
        <SenderAccountsModal accounts={senderAccounts} selectHandler={senderAccountSelect} />
        <ConfirmPaymentModal
          title={t('Electricity payment')}
          paymentInfo={electricQuery}
          currency={senderAccountCurrency}
          paymentTransactionHandler={electricTransaction}
          clearFields={clearFields}
          route={'electricPaymentStatus'}
          type='ELECTRIC'
        />
      </div>
      <div className='col-span-2 max-md:hidden'>
        <Currencies size='max-lg:text-xs text-sm' />
      </div>
    </div>
  );
}

export default Electric;
